// frontend/src/pages/admin/LearningDevelopment/components/CourseRequestsTab.js
// HR worklist for reviewing needs, arranging training, and recording outcomes.
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CalendarPlus, CheckCircle, XCircle, Award,
  BookOpen, FileText, Inbox, Ban, Clock, FileSignature, Download, Upload, X,
} from 'lucide-react';
import { InfoBadge } from '../../../../components/UI/InfoBadge';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import { TradeBadge } from '../../../../components/UI/TradeBadge';
import { CountBadge } from '../../../../components/UI/CountBadge';
import FormSelect from '../../../../components/UI/FormSelect';
import Modal from '../../../../components/UI/Modal';
import ConfirmModal from '../../../../components/UI/ConfirmModal';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import FilterButton from '../../../../components/UI/FilterButton';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import Pagination from '../../../../components/UI/Pagination';
import SearchableCombobox from '../../../../components/UI/SearchableCombobox';
import DatePickerField from '../../../../components/UI/DatePickerField';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import {
  usePaginatedCourseRequestHrInbox,
  usePaginatedCourseRequests,
  useCourseRequestDetail,
  useCourseRequestDecision,
  useConfirmCourseBooking,
  useCompleteCourseRequest,
  useCourseRequestStatusSummary,
  useTrainingProviders,
  useMasterCourses,
  useIssueSponsorshipAgreement,
} from '../../../../hooks/useCourseRequests';
import { staffAPI } from '../../../../services/api';
import toast from '../../../../utils/toast';
import { formatDate, parseLocalDate, toLocalISODate } from '../../../../utils/dateUtils';
import { formatCurrency } from '../../../../utils/numberUtils';
import { formatStoredProjectLabel } from '../../../../utils/projectDisplayUtils';
import {
  getDepartmentBadgeColor,
  getDepartmentShortName,
} from '../../../../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../../../../config/tradeConfig';
import {
  COURSE_SCHEDULE_PATTERN_OPTIONS,
  formatPreferredDays,
  formatPreferredSessions,
  isScheduleOnlyLegacyNote,
} from '../../../../constants/courseRequestSchedule';

const STATUS_FILTER_OPTIONS = [
  { value: 'pending_hod', label: 'Awaiting HOD' },
  { value: 'pending_hr', label: 'Needs review' },
  { value: 'approved', label: 'Awaiting booking' },
  { value: 'booked', label: 'Booked' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All requests' },
];

/** Stages HR acts on via hr-inbox (active pipeline, not archive). */
const WORKQUEUE_STAGES = new Set(['pending_hod', 'pending_hr', 'approved', 'booked']);

const PAGE_SIZE = 10;
const EMPTY_PAGE = {
  count: 0,
  next: null,
  previous: null,
  results: [],
};

const BOND_DURATION_OPTIONS = [
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: '18', label: '18 months' },
  { value: '24', label: '24 months' },
  { value: '36', label: '36 months' },
];

/** StatusBadge props — CourseRequest approved means HR still needs to book. */
const crBadgeProps = (item) => {
  const status = item?.status;
  if (status === 'pending_hod') {
    return { status: 'pending_hod', label: 'Awaiting HOD' };
  }
  if (status === 'pending_hr') {
    return { status: 'pending_hr', label: 'Needs review' };
  }
  if (status === 'approved') {
    return { status: 'awaiting_booking', label: 'Awaiting booking' };
  }
  if (status === 'booked') {
    return { status: 'booked', label: 'Booked' };
  }
  if (
    status === 'cancelled'
    && (item?.cancellation_reason || '').trim().toLowerCase() === 'cancelled by hr'
  ) {
    return { status: 'cancelled_by_hr', label: 'Cancelled by HR' };
  }
  return { status };
};

const MetricTile = ({ icon: Icon, color, value, label, sub, onClick, active = false }) => {
  const content = (
    <div className="metrics-card-content">
      <div className={`metrics-card-icon metrics-card-icon-${color}`}>
        <Icon className="w-4 h-4 sm:w-6 sm:h-6" />
      </div>
      <div className="metrics-card-text">
        <p className="metrics-card-value">{value}</p>
        <p className="metrics-card-label">{label}</p>
        {sub && <p className="text-[11px] sm:text-xs text-gray-400 truncate">{sub}</p>}
      </div>
    </div>
  );

  if (!onClick) {
    return <div className="metrics-card">{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`metrics-card w-full text-left transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
        active
          ? 'ring-2 ring-red-600 ring-offset-1'
          : 'hover:shadow-md'
      }`}
    >
      {content}
    </button>
  );
};

/** Read-only detail row — DS field-label + field-display (view mode). */
const DetailField = ({ label, children, multiline = false, className = '' }) => (
  <div className={className}>
    <dt className="field-label">{label}</dt>
    <dd className={`field-display ${multiline ? 'field-display-multiline' : ''} mt-0.5`}>
      {children}
    </dd>
  </div>
);

const formatSessionDates = (dates = []) =>
  (dates || []).map((date) => formatDate(date)).join(', ');

const formatCourseTime = (value) => (value ? String(value).slice(0, 5) : '');

/** White section card inside the gray-50 detail body — DS compact view spacing. */
const DetailSection = ({ title, children }) => (
  <section className="bg-white rounded-xl border border-gray-200 p-4">
    <h3 className="text-base font-semibold text-gray-900 mb-3">{title}</h3>
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {children}
    </dl>
  </section>
);

const filenameFromUrl = (url) => {
  if (!url) return '';
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() || '');
  } catch {
    return '';
  }
};

/**
 * DS-compliant optional file upload row for admin forms (booking confirmation,
 * certificate). Shows the file already on record with a view link, a
 * dashed dropzone to attach/replace, and the pending selection with a clear
 * affordance — instead of a bare `<input type="file">`.
 */
const AdminDocumentUploadField = ({
  file,
  onFileChange,
  existingUrl,
  existingLabel = 'On file',
  accept = '.pdf,image/*',
  helperText,
}) => {
  if (file) {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-900 truncate">{file.name}</span>
          </div>
          <button
            type="button"
            onClick={() => onFileChange(null)}
            className="icon-button-admin icon-button-sm flex-shrink-0"
            title="Remove selected file"
            aria-label="Remove selected file"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {existingUrl && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-sm text-gray-700 truncate">
              {filenameFromUrl(existingUrl) || existingLabel}
            </span>
          </div>
          <a
            href={existingUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 flex-shrink-0"
          >
            <Download className="w-3.5 h-3.5" /> View
          </a>
        </div>
      )}
      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={(e) => onFileChange(e.target.files?.[0] || null)}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <div className="rounded-xl border-2 border-dashed border-gray-300 hover:border-red-300 transition-colors text-center px-4 py-4">
          <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
          <p className="text-sm text-gray-600">
            {existingUrl ? 'Drop a file to replace, or ' : 'Drop file here, or '}
            <span className="text-red-600 font-medium">browse</span>
          </p>
        </div>
      </div>
      {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
    </div>
  );
};

const RequestDetailFields = ({ detail }) => (
  <DetailSection title="Training request">
    <DetailField label="Requested course" className="sm:col-span-2">
      <span className="font-medium text-gray-900">{detail.display_course_name}</span>
    </DetailField>
    {detail.requested_by_name && (
      <DetailField label="Requested by">{detail.requested_by_name}</DetailField>
    )}
    <DetailField
      label="Submitted"
      className={detail.requested_by_name ? '' : 'sm:col-span-2'}
    >
      {formatDate(detail.created_at)}
    </DetailField>
    <DetailField
      label="Purpose"
      multiline={Boolean(
        detail.purpose_code === 'other' ? detail.purpose : detail.purpose_notes,
      )}
      className="sm:col-span-2"
    >
      {detail.purpose_code ? (
        <>
          <span className="font-medium text-gray-900">{detail.purpose_code_display}</span>
          {(detail.purpose_code === 'other' ? detail.purpose : detail.purpose_notes) ? (
            <span className="block text-gray-600 mt-1 whitespace-pre-wrap">
              {detail.purpose_code === 'other' ? detail.purpose : detail.purpose_notes}
            </span>
          ) : null}
        </>
      ) : (
        <span className="whitespace-pre-wrap text-gray-800">{detail.purpose}</span>
      )}
    </DetailField>
    {detail.project_site && (
      <DetailField label="Project" className="sm:col-span-2">
        {formatStoredProjectLabel(detail.project_site)}
      </DetailField>
    )}
    {detail.preferred_days?.length > 0 && (
      <DetailField label="Requested days">
        {formatPreferredDays(detail.preferred_days)}
      </DetailField>
    )}
    {detail.preferred_timing?.length > 0 && (
      <DetailField label="Requested sessions">
        {formatPreferredSessions(detail.preferred_timing)}
      </DetailField>
    )}
    {detail.preferred_language && (
      <DetailField label="Preferred language">
        {detail.preferred_language_display}
      </DetailField>
    )}
  </DetailSection>
);

const BookingDetailFields = ({ detail }) => {
  const hasBooking = Boolean(
    detail.booked_course_name
    || detail.course_schedule_pattern
    || detail.course_start_date
    || detail.course_location
    || detail.training_provider_uen
    || (detail.course_amount != null && detail.course_amount !== ''),
  );
  if (!hasBooking) return null;

  return (
    <DetailSection title="Confirmed booking">
      {detail.booked_course_name && (
        <DetailField label="Confirmed course" className="sm:col-span-2">
          <span className="font-medium text-gray-900">{detail.booked_course_name}</span>
        </DetailField>
      )}
      {(detail.training_provider_name || detail.training_provider_uen) && (
        <DetailField label="Training provider" className="sm:col-span-2">
          {detail.training_provider_name || detail.training_provider_uen}
          {detail.training_provider_name && detail.training_provider_uen && (
            <span className="text-gray-500"> · {detail.training_provider_uen}</span>
          )}
        </DetailField>
      )}
      {detail.course_schedule_pattern ? (
        <DetailField label="Confirmed schedule" multiline className="sm:col-span-2">
          <span className="font-medium text-gray-900">{detail.course_schedule_display}</span>
          {detail.course_session_dates?.length > 0 && (
            <span className="block mt-1 text-gray-700">
              Sessions: {formatSessionDates(detail.course_session_dates)}
            </span>
          )}
          {detail.course_schedule_notes && (
            <span className="block mt-1 whitespace-pre-wrap text-gray-600">
              {detail.course_schedule_notes}
            </span>
          )}
        </DetailField>
      ) : detail.course_start_date ? (
        <DetailField label="Training period" className="sm:col-span-2">
          {formatDate(detail.course_start_date)}
          {detail.course_end_date && detail.course_end_date !== detail.course_start_date
            ? ` – ${formatDate(detail.course_end_date)}`
            : ''}
        </DetailField>
      ) : null}
      {detail.course_location && (
        <DetailField label="Venue" className="sm:col-span-2">
          {detail.course_location}
        </DetailField>
      )}
      {(detail.course_start_time || detail.course_end_time) && (
        <DetailField label="Session time">
          {[
            formatCourseTime(detail.course_start_time),
            formatCourseTime(detail.course_end_time),
          ].filter(Boolean).join(' – ')}
        </DetailField>
      )}
      {detail.course_hours != null && detail.course_hours !== '' && (
        <DetailField label="Duration">{Number(detail.course_hours)} hours</DetailField>
      )}
      {detail.booked_at && (
        <DetailField label="Booked">
          {formatDate(detail.booked_at)}
          {detail.booked_by_name ? ` · ${detail.booked_by_name}` : ''}
        </DetailField>
      )}
      {detail.course_amount != null && detail.course_amount !== '' && (
        <DetailField label="Course fee">{formatCurrency(detail.course_amount)}</DetailField>
      )}
      {(detail.bond_required
        || (detail.course_amount != null && detail.course_amount !== '')) && (
        <DetailField label="Sponsorship bond">
          {detail.bond_required
            ? `${detail.bond_duration_months} months`
            : 'Not required'}
        </DetailField>
      )}
      {detail.booking_confirmation_url && (
        <DetailField label="Booking confirmation">
          <a
            href={detail.booking_confirmation_url}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-red-600 hover:text-red-700"
          >
            View document
          </a>
        </DetailField>
      )}
    </DetailSection>
  );
};

const AGREEMENT_STATUS_LABEL = {
  pending_acknowledgment: 'Pending employee signature',
  acknowledged: 'Signed',
};

const AgreementDetailFields = ({ detail, onDownload, downloading }) => {
  if (!detail.agreement_status || detail.agreement_status === 'not_required') return null;
  const isAcknowledged = detail.agreement_status === 'acknowledged';

  return (
    <DetailSection title="Sponsorship agreement (FHR009)">
      <DetailField label="Status">
        <span className={isAcknowledged ? 'text-green-700 font-medium' : 'text-amber-700 font-medium'}>
          {AGREEMENT_STATUS_LABEL[detail.agreement_status] || detail.agreement_status}
        </span>
      </DetailField>
      {detail.agreement_terms?.course_fee != null && (
        <DetailField label="Sponsored amount">
          {formatCurrency(detail.agreement_terms.course_fee)}
        </DetailField>
      )}
      {detail.agreement_issued_by_name && (
        <DetailField label="Issued by">
          {detail.agreement_issued_by_name}
          {detail.agreement_issued_at ? ` · ${formatDate(detail.agreement_issued_at)}` : ''}
        </DetailField>
      )}
      {isAcknowledged && (
        <DetailField label="Signed">
          {detail.agreement_acknowledged_at ? formatDate(detail.agreement_acknowledged_at) : '—'}
        </DetailField>
      )}
      {isAcknowledged && detail.agreement_document_url && (
        <DetailField label="Signed document" className="sm:col-span-2">
          <button
            type="button"
            onClick={() => onDownload(detail.id)}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> Download signed PDF
          </button>
        </DetailField>
      )}
    </DetailSection>
  );
};

const ApprovalDetailFields = ({ detail }) => {
  const showHod = detail.hod_status && detail.hod_status !== 'not_required';
  const showHr = detail.hr_status && detail.hr_status !== 'not_required';
  if (!showHod && !showHr) return null;

  return (
    <DetailSection title="Approval history">
      {showHod && (
        <DetailField label="HOD review">
          <StatusBadge status={detail.hod_status} />
          {(detail.hod_approver_name || detail.hod_decided_at) && (
            <span className="block mt-1 text-gray-500">
              {[
                detail.hod_approver_name,
                detail.hod_decided_at ? formatDate(detail.hod_decided_at) : '',
              ]
                .filter(Boolean).join(' · ')}
            </span>
          )}
        </DetailField>
      )}
      {showHr && (
        <DetailField label="HR review">
          <StatusBadge status={detail.hr_status} />
          {(detail.hr_approver_name || detail.hr_decided_at) && (
            <span className="block mt-1 text-gray-500">
              {[
                detail.hr_approver_name,
                detail.hr_decided_at ? formatDate(detail.hr_decided_at) : '',
              ]
                .filter(Boolean).join(' · ')}
            </span>
          )}
        </DetailField>
      )}
      {detail.hod_comments && (
        <DetailField label="HOD comments" multiline className="sm:col-span-2">
          <span className="whitespace-pre-wrap">{detail.hod_comments}</span>
        </DetailField>
      )}
      {detail.hr_comments
        && detail.status !== 'rejected'
        && !isScheduleOnlyLegacyNote(detail.hr_comments)
        && detail.hr_comments !== detail.course_schedule_notes && (
        <DetailField label="HR comments" multiline className="sm:col-span-2">
          <span className="whitespace-pre-wrap">{detail.hr_comments}</span>
        </DetailField>
      )}
    </DetailSection>
  );
};

const OutcomeDetailFields = ({ detail }) => (
  <DetailSection title="Training outcome">
    <DetailField label="Result">
      {detail.result ? <StatusBadge status={detail.result} /> : 'Not recorded'}
    </DetailField>
    {detail.completed_at && (
      <DetailField label="Completed">{formatDate(detail.completed_at)}</DetailField>
    )}
    {detail.certificate_url ? (
      <DetailField label="Certificate" className="sm:col-span-2">
        <a
          href={detail.certificate_url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-red-600 hover:text-red-700"
        >
          View certificate
        </a>
      </DetailField>
    ) : detail.result === 'pass' && (
      <DetailField label="Certificate" className="sm:col-span-2">
        <span className="text-gray-500">
          Awaiting certificate — will link automatically once the worker&apos;s certificate is
          verified via Profile or Employee Management.
        </span>
      </DetailField>
    )}
    {detail.bond_status && (
      <DetailField label="Bond status">
        <StatusBadge status={detail.bond_status} />
      </DetailField>
    )}
  </DetailSection>
);

const ClosureDetailFields = ({ detail }) => {
  const isRejected = detail.status === 'rejected';
  const decisionBy = detail.hr_status === 'rejected'
    ? detail.hr_approver_name
    : detail.hod_approver_name;
  const decisionAt = detail.hr_status === 'rejected'
    ? detail.hr_decided_at
    : detail.hod_decided_at;

  return (
    <DetailSection title={isRejected ? 'Decision' : 'Cancellation'}>
      <DetailField
        label={isRejected ? 'Rejection reason' : 'Cancellation reason'}
        multiline
        className="sm:col-span-2"
      >
        <span className={isRejected ? 'text-red-700' : 'text-gray-800'}>
          {(isRejected ? detail.rejection_reason : detail.cancellation_reason)
            || 'No reason recorded'}
        </span>
      </DetailField>
      {(isRejected ? decisionBy || decisionAt : detail.cancelled_by_name || detail.cancelled_at) && (
        <DetailField label={isRejected ? 'Decided' : 'Cancelled'} className="sm:col-span-2">
          {(isRejected
            ? [decisionBy, decisionAt ? formatDate(decisionAt) : '']
            : [
                detail.cancelled_by_name,
                detail.cancelled_at ? formatDate(detail.cancelled_at) : '',
              ])
            .filter(Boolean).join(' · ')}
        </DetailField>
      )}
    </DetailSection>
  );
};

const CourseRequestsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id') || '';
  const isCompact = useIsMobile(1024);
  const worklistRef = useRef(null);
  const detailPanelRef = useRef(null);
  const detailBodyRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const restoreListScrollRef = useRef(false);
  const [stage, setStage] = useState('pending_hr');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [comments, setComments] = useState('');
  const [showRejectionReason, setShowRejectionReason] = useState(false);
  const [rejectionReasonError, setRejectionReasonError] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const stageSyncedForIdRef = useRef('');

  const isWorkqueue = WORKQUEUE_STAGES.has(stage);

  const { data: summary = {} } = useCourseRequestStatusSummary();

  const listParams = {
    page,
    page_size: PAGE_SIZE,
    ...(stage === 'all' ? {} : { status: stage }),
    ...(search.trim() ? { search: search.trim() } : {}),
  };
  const { data: workqueuePage = EMPTY_PAGE, isLoading: workqueueLoading } =
    usePaginatedCourseRequestHrInbox(
      listParams,
      { enabled: isWorkqueue },
    );
  const { data: archivePage = EMPTY_PAGE, isLoading: archiveLoading } =
    usePaginatedCourseRequests(
      listParams,
      { enabled: !isWorkqueue },
    );
  const pageData = isWorkqueue ? workqueuePage : archivePage;
  const stageList = pageData.results;
  const totalCount = pageData.count;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const isLoading = isWorkqueue ? workqueueLoading : archiveLoading;

  const stageCounts = useMemo(() => ({
    pending_hod: summary.pending_hod ?? 0,
    pending_hr: summary.pending_hr ?? 0,
    approved: summary.approved ?? 0,
    booked: summary.booked ?? 0,
    completed: summary.completed ?? 0,
    rejected: summary.rejected ?? 0,
    cancelled: summary.cancelled ?? 0,
    all: summary.total ?? 0,
  }), [summary]);

  const { data: detail, isLoading: detailLoading } = useCourseRequestDetail(selectedId, {
    enabled: !!selectedId,
  });

  // Deep-link / detail open: align the stage filter so the selected row
  // appears in the left worklist (self-service can open any status; admin
  // defaults to Needs review and previously hid Awaiting HOD entirely).
  useEffect(() => {
    if (!selectedId || !detail?.status) return;
    if (stageSyncedForIdRef.current === selectedId) return;
    const detailStatus = detail.status;
    const knownStage = STATUS_FILTER_OPTIONS.some((opt) => opt.value === detailStatus);
    if (!knownStage || detailStatus === 'all') return;
    stageSyncedForIdRef.current = selectedId;
    if (stage !== detailStatus && stage !== 'all') {
      setStage(detailStatus);
      setPage(1);
    }
  }, [selectedId, detail?.status, stage]);

  useEffect(() => {
    if (!selectedId) {
      stageSyncedForIdRef.current = '';
    }
  }, [selectedId]);
  const { data: providers = [] } = useTrainingProviders();
  const { data: masterCourses = [] } = useMasterCourses();
  const decisionMutation = useCourseRequestDecision();
  const bookingMutation = useConfirmCourseBooking();
  const completeMutation = useCompleteCourseRequest();
  const issueAgreementMutation = useIssueSponsorshipAgreement();
  const [downloadingAgreement, setDownloadingAgreement] = useState(false);
  const [showIssueAgreement, setShowIssueAgreement] = useState(false);
  const [agreementForm, setAgreementForm] = useState({
    institution: '',
    course_name: '',
    modules: '',
    duration: '',
    course_fee: '',
    bond_duration_months: '',
  });

  const [bookingForm, setBookingForm] = useState({
    training_provider_uen: '',
    booked_course_name: '',
    course_start_date: '',
    course_end_date: '',
    course_schedule_pattern: 'single_day',
    course_schedule_notes: '',
    course_location: '',
    course_hours: '',
    course_amount: '',
    bond_required: '',
    bond_duration_months: '',
  });
  const [bookingFile, setBookingFile] = useState(null);
  const [completeForm, setCompleteForm] = useState({
    result: 'pass',
    cert_issue_date: '',
    cert_expiry_date: '',
  });
  const [certFile, setCertFile] = useState(null);

  const selectedProvider = useMemo(
    () => (providers || []).find((p) => p.uen === bookingForm.training_provider_uen) || null,
    [providers, bookingForm.training_provider_uen],
  );

  const openDetail = (id) => {
    if (id && isCompact) {
      const scrollContainer = worklistRef.current?.closest('main');
      savedScrollTopRef.current = scrollContainer?.scrollTop || 0;
      restoreListScrollRef.current = false;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('id', id);
        else next.delete('id');
        if (!next.get('tab')) next.set('tab', 'course-requests');
        return next;
      },
      { replace: true },
    );
    setComments('');
    setShowRejectionReason(false);
    setRejectionReasonError('');
  };

  const closeCompactDetail = () => {
    restoreListScrollRef.current = true;
    openDetail('');
  };

  useEffect(() => {
    if (!isCompact) return undefined;

    const frame = window.requestAnimationFrame(() => {
      if (selectedId) {
        detailPanelRef.current?.scrollIntoView({ block: 'start' });
        return;
      }
      if (restoreListScrollRef.current) {
        const scrollContainer = worklistRef.current?.closest('main');
        scrollContainer?.scrollTo({ top: savedScrollTopRef.current, behavior: 'auto' });
        restoreListScrollRef.current = false;
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isCompact, selectedId]);

  // Desktop dual-pane: reset detail scroll when switching requests so HR
  // always starts at the header / decision context, not mid-previous record.
  useEffect(() => {
    if (isCompact || !selectedId) return;
    detailBodyRef.current?.scrollTo({ top: 0 });
  }, [isCompact, selectedId]);

  const changeStage = (nextStage) => {
    setStage(nextStage);
    setPage(1);
    setShowFilters(false);
    openDetail('');
  };

  const changeSearch = (nextSearch) => {
    setSearch(nextSearch);
    setPage(1);
    openDetail('');
  };

  const changePage = (nextPage) => {
    setPage(nextPage);
    openDetail('');
  };

  const openBooking = () => {
    if (!detail) return;
    const lib = (masterCourses || []).find((c) => c.code === detail.master_course_code);
    setBookingForm({
      training_provider_uen: detail.training_provider_uen || '',
      booked_course_name: detail.booked_course_name || lib?.name || detail.display_course_name || '',
      course_start_date: detail.course_start_date || '',
      course_end_date: detail.course_end_date || '',
      course_schedule_pattern: detail.course_schedule_pattern
        || (detail.course_start_date === detail.course_end_date
          ? 'single_day'
          : 'consecutive_days'),
      course_schedule_notes: detail.course_schedule_notes || '',
      course_location: detail.course_location || '',
      course_hours: detail.course_hours != null ? String(detail.course_hours) : '',
      course_amount: detail.course_amount != null ? String(detail.course_amount) : '',
      bond_required: detail.bond_required ? 'true' : '',
      bond_duration_months: detail.bond_duration_months != null ? String(detail.bond_duration_months) : '',
    });
    setBookingFile(null);
    setShowBooking(true);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(bookingForm).forEach(([k, v]) => {
      if (v !== '' && v != null) fd.append(k, v);
    });
    if (bookingFile) fd.append('booking_confirmation', bookingFile);
    await bookingMutation.mutateAsync({ id: selectedId, formData: fd });
    setShowBooking(false);
    openDetail('');
  };

  const openIssueAgreement = () => {
    if (!detail) return;
    setAgreementForm({
      institution: detail.agreement_terms?.institution || '',
      course_name: detail.agreement_terms?.course_name || detail.display_course_name || '',
      modules: detail.agreement_terms?.modules || '',
      duration: detail.agreement_terms?.duration || '',
      course_fee: detail.agreement_terms?.course_fee
        || (detail.course_amount != null ? String(detail.course_amount) : ''),
      bond_duration_months: detail.agreement_terms?.bond_duration_months != null
        ? String(detail.agreement_terms.bond_duration_months)
        : '',
    });
    setShowIssueAgreement(true);
  };

  const submitIssueAgreement = async (e) => {
    e.preventDefault();
    const payload = {
      institution: agreementForm.institution,
      course_name: agreementForm.course_name,
      modules: agreementForm.modules,
      duration: agreementForm.duration,
      course_fee: agreementForm.course_fee,
    };
    if (agreementForm.bond_duration_months) {
      payload.bond_duration_months = agreementForm.bond_duration_months;
    }
    await issueAgreementMutation.mutateAsync({ id: selectedId, data: payload });
    setShowIssueAgreement(false);
    openDetail(selectedId);
  };

  const handleDownloadAgreement = async (id) => {
    if (!id || downloadingAgreement) return;
    setDownloadingAgreement(true);
    try {
      const response = await staffAPI.downloadCourseAgreement(id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Sponsorship_Agreement_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error('Failed to download the sponsorship agreement');
    } finally {
      setDownloadingAgreement(false);
    }
  };

  const submitComplete = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('result', completeForm.result);
    if (completeForm.cert_issue_date) fd.append('cert_issue_date', completeForm.cert_issue_date);
    if (completeForm.cert_expiry_date) fd.append('cert_expiry_date', completeForm.cert_expiry_date);
    if (certFile) fd.append('certificate', certFile);
    await completeMutation.mutateAsync({ id: selectedId, formData: fd });
    setShowComplete(false);
    openDetail('');
  };

  const act = async (action) => {
    const needsReason = action === 'reject-hr' || action === 'reject-hod';
    if (needsReason && !comments.trim()) {
      setShowRejectionReason(true);
      setRejectionReasonError('Provide a reason before rejecting this request.');
      return;
    }
    await decisionMutation.mutateAsync({
      id: selectedId,
      action,
      comments: needsReason ? comments.trim() : (comments.trim() || ''),
    });
    setComments('');
    setShowRejectionReason(false);
    setRejectionReasonError('');
    openDetail('');
  };

  const emptyCopy = () => {
    if (search.trim()) {
      return {
        title: 'No matching requests',
        body: 'Try a different name, staff ID, or course.',
        icon: FileText,
        tone: 'neutral',
      };
    }
    if (stage === 'pending_hod') {
      return {
        title: 'No requests awaiting HOD',
        body: 'Submitted requests waiting on department head approval appear here.',
        icon: Clock,
        tone: 'neutral',
      };
    }
    if (stage === 'pending_hr') {
      return {
        title: 'All caught up',
        body: 'There are no course requests awaiting HR review.',
        icon: CheckCircle,
        tone: 'success',
      };
    }
    if (stage === 'approved') {
      return {
        title: 'Nothing awaiting booking',
        body: 'Portal-approved requests that still need provider booking appear here.',
        icon: CalendarPlus,
        tone: 'neutral',
      };
    }
    if (stage === 'booked') {
      return {
        title: 'No booked courses awaiting an outcome',
        body: 'Confirmed courses remain here until their result is recorded.',
        icon: BookOpen,
        tone: 'neutral',
      };
    }
    if (stage === 'cancelled') {
      return {
        title: 'No cancelled requests',
        body: 'Cancelled and withdrawn requests appear here.',
        icon: Ban,
        tone: 'neutral',
      };
    }
    return {
      title: 'No course requests',
      body: 'Nothing matches this filter.',
      icon: Inbox,
      tone: 'neutral',
    };
  };

  const empty = emptyCopy();
  const EmptyIcon = empty.icon;
  const showDetailTrade = Boolean(
    (detail?.trade_code || detail?.trade_name)
    && !isTradeRedundantWithDepartment(
      detail?.department_name,
      detail?.trade_code,
      detail?.trade_name,
    ),
  );

  return (
    <div className="space-y-6">
      {(!isCompact || !selectedId) && (
        <>
          <div>
            <div className="flex items-center">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                Course requests
              </h2>
              <CountBadge
                count={totalCount}
                theme="admin"
                size="md"
                className="ml-3 flex-shrink-0"
              />
            </div>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              Track the full pipeline — HOD approval through booking and course outcomes.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <MetricTile
              icon={Clock}
              color="purple"
              value={stageCounts.pending_hod}
              label="Awaiting HOD"
              sub="Submitted, department head pending"
              active={stage === 'pending_hod'}
              onClick={() => changeStage('pending_hod')}
            />
            <MetricTile
              icon={AlertTriangle}
              color="orange"
              value={stageCounts.pending_hr}
              label="Needs review"
              sub="Awaiting HR decision"
              active={stage === 'pending_hr'}
              onClick={() => changeStage('pending_hr')}
            />
            <MetricTile
              icon={CalendarPlus}
              color="blue"
              value={stageCounts.approved}
              label="Awaiting booking"
              sub="Approved, provider not confirmed"
              active={stage === 'approved'}
              onClick={() => changeStage('approved')}
            />
            <MetricTile
              icon={BookOpen}
              color="green"
              value={stageCounts.booked}
              label="Awaiting outcome"
              sub="Booked course, result not recorded"
              active={stage === 'booked'}
              onClick={() => changeStage('booked')}
            />
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <DebouncedSearchInput
              value={search}
              onSearch={changeSearch}
              placeholder="Search by name, staff ID, or course…"
              theme="admin"
              className="w-full max-w-xl"
            />
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={stage === 'all' ? 0 : 1}
              onClick={() => setShowFilters((open) => !open)}
              className="self-start sm:self-auto flex-shrink-0"
            />
          </div>

          {showFilters && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="max-w-xs">
                <label className="form-label-admin">Request status</label>
                <FormSelect
                  options={STATUS_FILTER_OPTIONS}
                  value={stage}
                  onChange={changeStage}
                  theme="admin"
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* List + detail — desktop: fixed-height dual panes (list scroll ≠ detail
          scroll) so HR can pick a bottom-row request without losing Approve. */}
      <div
        ref={worklistRef}
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 lg:gap-6 lg:h-[calc(100dvh-16rem)] lg:min-h-[28rem] lg:overflow-hidden"
      >
        <div
          className={`${
            isCompact && selectedId ? 'hidden' : 'space-y-3'
          } lg:min-h-0 lg:overflow-y-auto lg:pr-1`}
        >
          {isLoading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner size="md" />
            </div>
          ) : stageList.length === 0 ? (
            <div className="text-center py-12">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  empty.tone === 'success' ? 'bg-green-100' : 'bg-gray-100'
                }`}
              >
                <EmptyIcon
                  className={`w-6 h-6 ${
                    empty.tone === 'success' ? 'text-green-600' : 'text-gray-400'
                  }`}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{empty.title}</h3>
              <p className="text-sm text-gray-500">{empty.body}</p>
            </div>
          ) : (
            stageList.map((item) => {
              const isSelected = selectedId === String(item.id);
              const showTrade = (item.trade_code || item.trade_name)
                && !isTradeRedundantWithDepartment(
                  item.department_name,
                  item.trade_code,
                  item.trade_name,
                );
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => openDetail(item.id)}
                  className={`w-full text-left border border-gray-200 rounded-xl p-4 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${
                    isSelected
                      ? 'bg-red-50 border-l-4 border-l-red-600'
                      : 'bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-baseline gap-2 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate">
                          {item.applicant_name || item.applicant_staff_id || 'Unknown applicant'}
                        </span>
                        {item.applicant_name && item.applicant_staff_id && (
                          <span className="text-xs text-gray-500 font-mono flex-shrink-0">
                            {item.applicant_staff_id}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <StatusBadge {...crBadgeProps(item)} />
                    </div>
                  </div>
                  {(item.department_name || showTrade) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {item.department_name && (
                        <InfoBadge
                          color={getDepartmentBadgeColor(item.department_name)}
                          label={getDepartmentShortName(item.department_name)}
                          title={item.department_name}
                          size="xs"
                        />
                      )}
                      {showTrade && (
                        <TradeBadge
                          code={item.trade_code}
                          name={item.trade_name}
                          size="xs"
                        />
                      )}
                    </div>
                  )}
                  <p className="mt-3 text-sm font-medium text-gray-900 line-clamp-2">
                    {item.display_course_name}
                  </p>
                  {(item.purpose_code_display || item.purpose || item.project_site) && (
                    <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">
                      {item.purpose_code_display || item.purpose}
                      {(item.purpose_code_display || item.purpose) && item.project_site ? ' · ' : ''}
                      {item.project_site ? formatStoredProjectLabel(item.project_site) : ''}
                    </p>
                  )}
                  <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between gap-3 text-xs text-gray-400">
                    <span className="truncate">{item.request_number}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3" />
                      Submitted {formatDate(item.created_at)}
                    </span>
                  </div>
                  {item.agreement_status === 'pending_acknowledgment' && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium">
                      <FileSignature className="w-3 h-3" /> Awaiting employee signature
                    </div>
                  )}
                </button>
              );
            })
          )}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={changePage}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            theme="admin"
            noun="requests"
            className="!rounded-2xl"
          />
        </div>

        <div
          ref={detailPanelRef}
          className={`${
            isCompact && !selectedId ? 'hidden' : ''
          } lg:min-h-0 lg:h-full lg:overflow-hidden lg:flex lg:flex-col`}
        >
          {isCompact && selectedId && (
            <button
              type="button"
              onClick={closeCompactDetail}
              className="btn btn-admin-outline mb-3 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to requests
            </button>
          )}
          {!selectedId ? (
            <div className="border border-gray-200 rounded-2xl bg-white shadow-sm p-8 text-center lg:flex-1 lg:flex lg:flex-col lg:items-center lg:justify-center">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-6 h-6 text-gray-400" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Select a request</h3>
              <p className="text-sm text-gray-500">
                Choose a course request to review details and take the next HR action.
              </p>
            </div>
          ) : detailLoading || !detail ? (
            <div className="border border-gray-200 rounded-2xl bg-white shadow-sm flex justify-center items-center py-16 lg:flex-1">
              <LoadingSpinner size="md" />
            </div>
          ) : (
            <div className="border border-gray-200 rounded-2xl bg-white shadow-sm overflow-clip lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="flex items-baseline gap-2 min-w-0 mt-0.5">
                      <span className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                        {detail.applicant_name || detail.applicant_staff_id || 'Unknown applicant'}
                      </span>
                      {detail.applicant_name && detail.applicant_staff_id && (
                        <span className="text-sm font-normal text-gray-500 font-mono flex-shrink-0">
                          {detail.applicant_staff_id}
                        </span>
                      )}
                    </h3>
                    {detail.request_number && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {detail.request_number}
                      </p>
                    )}
                    {(detail.department_name || showDetailTrade) && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {detail.department_name && (
                          <InfoBadge
                            color={getDepartmentBadgeColor(detail.department_name)}
                            label={detail.department_name}
                            title={detail.department_name}
                            size="md"
                          />
                        )}
                        {showDetailTrade && (
                          <TradeBadge
                            code={detail.trade_code}
                            name={detail.trade_name}
                            size="md"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <StatusBadge {...crBadgeProps(detail)} />
                  </div>
                </div>
              </div>

              <div
                ref={detailBodyRef}
                className="bg-gray-50 p-3 sm:p-4 space-y-3 lg:flex-1 lg:min-h-0 lg:overflow-y-auto"
              >
                {['rejected', 'cancelled'].includes(detail.status) && (
                  <ClosureDetailFields detail={detail} />
                )}
                {detail.status === 'completed' && (
                  <OutcomeDetailFields detail={detail} />
                )}
                {['booked', 'completed', 'cancelled'].includes(detail.status) && (
                  <BookingDetailFields detail={detail} />
                )}
                {['pending_hod', 'pending_hr', 'approved'].includes(detail.status) && (
                  <RequestDetailFields detail={detail} />
                )}
                {['booked', 'completed', 'rejected', 'cancelled'].includes(detail.status) && (
                  <RequestDetailFields detail={detail} />
                )}
                <ApprovalDetailFields detail={detail} />
                {['approved', 'booked', 'completed'].includes(detail.status) && (
                  <AgreementDetailFields
                    detail={detail}
                    onDownload={handleDownloadAgreement}
                    downloading={downloadingAgreement}
                  />
                )}
              </div>

              {['pending_hod', 'pending_hr', 'approved', 'booked'].includes(detail.status) && (
                <div className="border-t border-gray-200 bg-white p-3 sm:p-4 space-y-3 flex-shrink-0">
                    {detail.status === 'pending_hod' && (
                      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <Clock className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-900">
                          Waiting on the applicant&apos;s HOD. HR can approve or reject on
                          their behalf when needed; otherwise this moves to Needs review
                          after HOD approval.
                        </p>
                      </div>
                    )}

                    {(detail.status === 'pending_hr' || detail.status === 'pending_hod')
                      && showRejectionReason && (
                      <div>
                        <label htmlFor="course-request-rejection-reason" className="form-label-admin">
                          Rejection reason <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          id="course-request-rejection-reason"
                          value={comments}
                          onChange={(e) => {
                            setComments(e.target.value);
                            setRejectionReasonError('');
                          }}
                          rows={2}
                          placeholder="Explain why this request cannot proceed"
                          className="textarea-admin w-full !min-h-[64px]"
                          aria-invalid={Boolean(rejectionReasonError)}
                        />
                        {rejectionReasonError && (
                          <p className="mt-1 text-xs text-red-600">{rejectionReasonError}</p>
                        )}
                      </div>
                    )}

                    {detail.status === 'pending_hod' && (
                      <div className="flex gap-2 btn-row-spread">
                        <button
                          type="button"
                          disabled={decisionMutation.isPending}
                          onClick={() => act('reject-hod')}
                          className="btn btn-danger"
                        >
                          <XCircle className="w-4 h-4" />
                          {showRejectionReason ? 'Confirm rejection' : 'Reject'}
                        </button>
                        <button
                          type="button"
                          disabled={decisionMutation.isPending}
                          onClick={() => act('approve-hod')}
                          className="btn btn-success"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve as HOD
                        </button>
                      </div>
                    )}

                    {detail.status === 'pending_hr' && (
                      <div className="flex gap-2 btn-row-spread">
                        <button
                          type="button"
                          disabled={decisionMutation.isPending}
                          onClick={() => act('reject-hr')}
                          className="btn btn-danger"
                        >
                          <XCircle className="w-4 h-4" />
                          {showRejectionReason ? 'Confirm rejection' : 'Reject'}
                        </button>
                        <button
                          type="button"
                          disabled={decisionMutation.isPending}
                          onClick={() => act('approve-hr')}
                          className="btn btn-success"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve
                        </button>
                      </div>
                    )}

                    {detail.status === 'approved' && (
                      <>
                        {detail.agreement_status === 'pending_acknowledgment' && (
                          <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-900">
                              Sponsorship agreement issued — booking is blocked until{' '}
                              {detail.applicant_name || 'the employee'} signs it in the self-service portal.
                            </p>
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <button
                            type="button"
                            onClick={openIssueAgreement}
                            className="btn btn-admin-outline w-full"
                          >
                            <FileSignature className="w-4 h-4" />
                            {detail.agreement_status === 'not_required'
                              ? 'Issue sponsorship agreement'
                              : 'Re-issue sponsorship agreement'}
                          </button>
                          <button
                            type="button"
                            onClick={openBooking}
                            disabled={detail.agreement_status === 'pending_acknowledgment'}
                            title={
                              detail.agreement_status === 'pending_acknowledgment'
                                ? 'Blocked until the employee signs the sponsorship agreement'
                                : undefined
                            }
                            className="btn btn-admin-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CalendarPlus className="w-4 h-4" />
                            Confirm booking
                          </button>
                          {detail.can_cancel && (
                            <button
                              type="button"
                              disabled={decisionMutation.isPending}
                              onClick={() => setShowCancelConfirm(true)}
                              className="btn btn-admin-outline w-full"
                            >
                              <Ban className="w-4 h-4" />
                              Cancel request
                            </button>
                          )}
                        </div>
                      </>
                    )}

                    {detail.status === 'booked' && (
                      <div className="flex flex-col gap-2">
                        {detail.agreement_status === 'acknowledged' && !detail.booking_confirmation_url && (
                          <div className="flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 p-3">
                            <FileText className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-900">
                              No booking confirmation on file yet. Upload the training
                              provider&apos;s confirmation via <strong>Update booking</strong> once
                              you receive it.
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={openBooking}
                          className="btn btn-admin-outline w-full"
                        >
                          <CalendarPlus className="w-4 h-4" />
                          Update booking
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCompleteForm({
                              result: 'pass',
                              cert_issue_date: '',
                              cert_expiry_date: '',
                            });
                            setCertFile(null);
                            setShowComplete(true);
                          }}
                          className="btn btn-admin-primary w-full"
                        >
                          <Award className="w-4 h-4" />
                          Record result
                        </button>
                        {detail.can_cancel && (
                          <button
                            type="button"
                            disabled={decisionMutation.isPending}
                            onClick={() => setShowCancelConfirm(true)}
                            className="btn btn-admin-outline w-full"
                          >
                            <Ban className="w-4 h-4" />
                            Cancel request
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
        </div>
      </div>

      {/* Confirm booking */}
      <Modal
        isOpen={showBooking}
        onClose={() => setShowBooking(false)}
        title="Confirm booking"
        size="lg"
        theme="admin"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowBooking(false)}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="course-request-booking-form"
              disabled={bookingMutation.isPending}
              className="btn btn-admin-primary"
            >
              {bookingMutation.isPending ? 'Saving…' : 'Confirm booking'}
            </button>
          </>
        )}
      >
        <form id="course-request-booking-form" onSubmit={submitBooking} className="space-y-4">
          <div className="form-group">
            <label className="form-label-admin">Training provider</label>
            <SearchableCombobox
              theme="admin"
              variant="form"
              className="w-full"
              items={providers || []}
              value={bookingForm.training_provider_uen}
              onChange={(uen) => setBookingForm((p) => ({
                ...p,
                training_provider_uen: uen || '',
              }))}
              valueFn={(p) => p.uen}
              displayFn={(p) => p.trading_name || p.name || p.uen}
              displayValue={
                selectedProvider
                  ? (selectedProvider.trading_name || selectedProvider.name || selectedProvider.uen)
                  : ''
              }
              placeholder="Search provider…"
              searchPlaceholder="Type to search…"
              clearLabel="Clear provider"
            />
          </div>
          <div className="form-group">
            <label className="form-label-admin">
              Confirmed course <span className="text-red-500">*</span>
            </label>
            <input
              required
              value={bookingForm.booked_course_name}
              onChange={(e) => setBookingForm((p) => ({ ...p, booked_course_name: e.target.value }))}
              className="input-admin w-full"
            />
          </div>
          <div className="form-group">
            <label className="form-label-admin">
              Attendance pattern <span className="text-red-500">*</span>
            </label>
            <FormSelect
              theme="admin"
              value={bookingForm.course_schedule_pattern}
              onChange={(value) => setBookingForm((previous) => ({
                ...previous,
                course_schedule_pattern: value,
                course_end_date: value === 'single_day'
                  ? previous.course_start_date
                  : previous.course_end_date,
                course_schedule_notes: value === 'custom'
                  ? previous.course_schedule_notes
                  : '',
              }))}
              options={COURSE_SCHEDULE_PATTERN_OPTIONS}
              placeholder="Select attendance pattern"
              required
            />
            <p className="form-help">
              HR will see the actual attendance dates, not just the overall date range.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">
                {bookingForm.course_schedule_pattern.startsWith('weekly')
                  || bookingForm.course_schedule_pattern === 'alternate_sundays'
                  ? 'First session'
                  : 'Start date'}
                {' '}<span className="text-red-500">*</span>
              </label>
              <DatePickerField
                theme="admin"
                selected={parseLocalDate(bookingForm.course_start_date)}
                onChange={(date) => setBookingForm((p) => ({
                  ...p,
                  course_start_date: date ? toLocalISODate(date) : '',
                  course_end_date: p.course_schedule_pattern === 'single_day'
                    ? (date ? toLocalISODate(date) : '')
                    : p.course_end_date,
                }))}
                placeholderText="Select start date"
                required
                maxDate={parseLocalDate(bookingForm.course_end_date)}
              />
            </div>
            {bookingForm.course_schedule_pattern !== 'single_day' && (
              <div className="form-group">
              <label className="form-label-admin">
                {bookingForm.course_schedule_pattern.startsWith('weekly')
                  || bookingForm.course_schedule_pattern === 'alternate_sundays'
                  ? 'Last session'
                  : 'End date'}
              </label>
              <DatePickerField
                theme="admin"
                selected={parseLocalDate(bookingForm.course_end_date)}
                onChange={(date) => setBookingForm((p) => ({
                  ...p,
                  course_end_date: date ? toLocalISODate(date) : '',
                }))}
                placeholderText="Select end date"
                isClearable
                minDate={parseLocalDate(bookingForm.course_start_date)}
              />
              </div>
            )}
          </div>
          {bookingForm.course_schedule_pattern === 'custom' && (
            <div className="form-group">
              <label className="form-label-admin">
                Timetable details <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={3}
                value={bookingForm.course_schedule_notes}
                onChange={(event) => setBookingForm((previous) => ({
                  ...previous,
                  course_schedule_notes: event.target.value,
                }))}
                className="textarea-admin w-full"
                placeholder="List the session dates, or state where the confirmed timetable is attached."
              />
            </div>
          )}
          <div className="form-group">
            <label className="form-label-admin">Location</label>
            <input
              value={bookingForm.course_location}
              onChange={(e) => setBookingForm((p) => ({ ...p, course_location: e.target.value }))}
              className="input-admin w-full"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">Hours</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={bookingForm.course_hours}
                onChange={(e) => setBookingForm((p) => ({ ...p, course_hours: e.target.value }))}
                className="input-admin w-full"
              />
            </div>
            <div className="form-group">
              <label className="form-label-admin">Amount (SGD)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={bookingForm.course_amount}
                onChange={(e) => setBookingForm((p) => ({ ...p, course_amount: e.target.value }))}
                className="input-admin w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">Sponsorship bond</label>
              <FormSelect
                theme="admin"
                value={bookingForm.bond_required}
                onChange={(val) => setBookingForm((p) => ({
                  ...p,
                  bond_required: val,
                  bond_duration_months: val === 'true' ? (p.bond_duration_months || '6') : '',
                }))}
                options={[
                  { value: '', label: 'Not required' },
                  { value: 'true', label: 'Required' },
                ]}
              />
            </div>
            {bookingForm.bond_required === 'true' && (
              <div className="form-group">
                <label className="form-label-admin">Bond duration</label>
                <FormSelect
                  theme="admin"
                  value={bookingForm.bond_duration_months}
                  onChange={(val) => setBookingForm((p) => ({ ...p, bond_duration_months: val }))}
                  options={BOND_DURATION_OPTIONS}
                />
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label-admin">
              Booking confirmation <span className="font-normal text-gray-400">optional</span>
            </label>
            <AdminDocumentUploadField
              file={bookingFile}
              onFileChange={setBookingFile}
              existingUrl={detail?.booking_confirmation_url}
              existingLabel="Booking confirmation on file"
              helperText="Upload the confirmation the training provider sent once you receive it."
            />
          </div>
        </form>
      </Modal>

      {/* Record result */}
      <Modal
        isOpen={showComplete}
        onClose={() => setShowComplete(false)}
        title="Record result"
        size="md"
        theme="admin"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowComplete(false)}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="course-request-complete-form"
              disabled={completeMutation.isPending}
              className="btn btn-admin-primary"
            >
              {completeMutation.isPending ? 'Saving…' : 'Save result'}
            </button>
          </>
        )}
      >
        <form id="course-request-complete-form" onSubmit={submitComplete} className="space-y-4">
          <div className="form-group">
            <label className="form-label-admin">Result</label>
            <FormSelect
              theme="admin"
              value={completeForm.result}
              onChange={(val) => setCompleteForm((p) => ({ ...p, result: val }))}
              options={[
                { value: 'pass', label: 'Pass' },
                { value: 'fail', label: 'Fail' },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">Cert issue date</label>
              <DatePickerField
                theme="admin"
                selected={parseLocalDate(completeForm.cert_issue_date)}
                onChange={(date) => setCompleteForm((p) => ({
                  ...p,
                  cert_issue_date: date ? toLocalISODate(date) : '',
                }))}
                placeholderText="Select date"
                isClearable
              />
            </div>
            <div className="form-group">
              <label className="form-label-admin">Cert expiry</label>
              <DatePickerField
                theme="admin"
                selected={parseLocalDate(completeForm.cert_expiry_date)}
                onChange={(date) => setCompleteForm((p) => ({
                  ...p,
                  cert_expiry_date: date ? toLocalISODate(date) : '',
                }))}
                placeholderText="Select date"
                isClearable
                minDate={parseLocalDate(completeForm.cert_issue_date)}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label-admin">
              Certificate file <span className="font-normal text-gray-400">optional</span>
            </label>
            <AdminDocumentUploadField
              file={certFile}
              onFileChange={setCertFile}
              existingUrl={detail?.certificate_url}
              existingLabel="Certificate on file"
              helperText={
                completeForm.result === 'pass'
                  ? "If the worker hasn't received it yet, leave this blank — it will link "
                    + 'automatically once their certificate is verified in Profile / Employee Management.'
                  : undefined
              }
            />
          </div>
        </form>
      </Modal>

      {/* Issue sponsorship agreement */}
      <Modal
        isOpen={showIssueAgreement}
        onClose={() => setShowIssueAgreement(false)}
        title="Issue sponsorship agreement (FHR009)"
        size="md"
        theme="admin"
        footer={(
          <>
            <button
              type="button"
              onClick={() => setShowIssueAgreement(false)}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="course-request-agreement-form"
              disabled={issueAgreementMutation.isPending}
              className="btn btn-admin-primary"
            >
              {issueAgreementMutation.isPending ? 'Issuing…' : 'Issue & notify employee'}
            </button>
          </>
        )}
      >
        <form id="course-request-agreement-form" onSubmit={submitIssueAgreement} className="space-y-4">
          <p className="text-xs text-gray-500">
            The employee will be notified to review and sign the agreement in the self-service
            portal. Booking is blocked until they acknowledge it.
          </p>
          <div className="form-group">
            <label className="form-label-admin">Institution</label>
            <input
              value={agreementForm.institution}
              onChange={(e) => setAgreementForm((p) => ({ ...p, institution: e.target.value }))}
              className="input-admin w-full"
            />
          </div>
          <div className="form-group">
            <label className="form-label-admin">
              Course name <span className="text-red-500">*</span>
            </label>
            <input
              required
              value={agreementForm.course_name}
              onChange={(e) => setAgreementForm((p) => ({ ...p, course_name: e.target.value }))}
              className="input-admin w-full"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">Modules</label>
              <input
                value={agreementForm.modules}
                onChange={(e) => setAgreementForm((p) => ({ ...p, modules: e.target.value }))}
                className="input-admin w-full"
                placeholder="e.g. 3"
              />
            </div>
            <div className="form-group">
              <label className="form-label-admin">Course duration</label>
              <input
                value={agreementForm.duration}
                onChange={(e) => setAgreementForm((p) => ({ ...p, duration: e.target.value }))}
                className="input-admin w-full"
                placeholder="e.g. 5 days"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin">
                Sponsored course fee (SGD) <span className="text-red-500">*</span>
              </label>
              <input
                required
                type="number"
                step="0.01"
                min="0.01"
                value={agreementForm.course_fee}
                onChange={(e) => setAgreementForm((p) => ({ ...p, course_fee: e.target.value }))}
                className="input-admin w-full"
              />
              <p className="form-help">Above $500 requires a bond; the duration is computed automatically.</p>
            </div>
            <div className="form-group">
              <label className="form-label-admin">Bond duration override</label>
              <FormSelect
                theme="admin"
                value={agreementForm.bond_duration_months}
                onChange={(val) => setAgreementForm((p) => ({ ...p, bond_duration_months: val }))}
                options={[
                  { value: '', label: 'Auto (recommended)' },
                  ...BOND_DURATION_OPTIONS,
                ]}
              />
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={showCancelConfirm}
        theme="admin"
        variant="danger"
        title="Cancel this course request"
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        isLoading={decisionMutation.isPending}
        onCancel={() => {
          setShowCancelConfirm(false);
          setCancelReason('');
        }}
        onConfirm={async () => {
          if (!selectedId) return;
          await decisionMutation.mutateAsync({
            id: selectedId,
            action: 'cancel',
            comments: cancelReason,
          });
          setShowCancelConfirm(false);
          setCancelReason('');
          setComments('');
          openDetail('');
        }}
      >
        <p className="text-sm text-gray-700">
          {detail?.status === 'booked'
            ? 'This withdraws the portal request. Confirm with the training provider if a seat was already paid.'
            : 'This withdraws the request before a provider booking is confirmed.'}
        </p>
        <textarea
          className="textarea-admin w-full mt-3"
          rows={3}
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
          placeholder="Optional reason"
        />
      </ConfirmModal>
    </div>
  );
};

export default CourseRequestsTab;
